import React, { useState, useRef, useEffect } from "react";
import { Loader2, RotateCw, Camera, MousePointer2, AlertCircle } from "lucide-react";
import { authenticatedFetch } from "../utils/api";
import { API_HOST } from "../utils/constants";

const Orbit3DViewer = ({ initialImage, onCapture }) => {
  const [status, setStatus] = useState("idle"); // idle, generating, polling, ready, failed
  const [videoUrl, setVideoUrl] = useState(null);
  const [rotation, setRotation] = useState(0); // 0-100 (模拟角度)
  const [errorMsg, setErrorMsg] = useState("");
  
  // ✅ 1. 新增：宽高比状态，默认为 16/9
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const pollingTimerRef = useRef(null);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
    };
  }, []);

  // ... (中间的 startOrbitGeneration 和 startPolling 代码保持不变，省略以节省篇幅) ...
  // 1. 开始生成 (调用真实接口)
  const startOrbitGeneration = async () => {
    if (!initialImage) return;
    setStatus("generating");
    setErrorMsg("");
    
    try {
      console.log("🚀 发起 3D 环绕生成请求...");
      // authenticatedFetch 已处理 ApiResult，直接返回数据
      const data = await authenticatedFetch(`${API_HOST}/api/video/generate-orbit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl: initialImage }),
      });
      
      const { taskId, provider } = data;
      
      console.log(`✅ 任务提交成功 TaskID: ${taskId}, 开始轮询...`);
      setStatus("polling");
      startPolling(taskId, provider || "vidu");

    } catch (e) {
      console.error("生成请求失败:", e);
      setErrorMsg(e.message);
      setStatus("failed");
    }
  };

  const startPolling = (taskId, provider) => {
    let pollCount = 0;
    const maxPolls = 200; // 最多轮询 200 次 (10 分钟)

    pollingTimerRef.current = setInterval(async () => {
      pollCount++;

      // 超时保护
      if (pollCount >= maxPolls) {
        clearInterval(pollingTimerRef.current);
        setErrorMsg("生成超时，请重试");
        setStatus("failed");
        return;
      }

      try {
        // authenticatedFetch 已处理 ApiResult，直接返回数据
        const data = await authenticatedFetch(
          `${API_HOST}/api/video/status?provider=${provider}&taskId=${taskId}`
        );

        if (data.status === "success") {
          clearInterval(pollingTimerRef.current);
          setVideoUrl(data.videoUrl);
          setStatus("ready");
        } else if (data.status === "failed") {
          clearInterval(pollingTimerRef.current);
          setErrorMsg(data.errorMessage || "生成失败");
          setStatus("failed");
        }
      } catch (e) {
        console.error("轮询出错:", e);
        // 错误时也清理 interval，避免泄漏
        clearInterval(pollingTimerRef.current);
        setErrorMsg("轮询失败: " + e.message);
        setStatus("failed");
      }
    }, 3000);
  };

  const handleMouseMove = (e) => {
    if (e.buttons !== 1 || status !== "ready" || !videoRef.current) return;
    const sensitivity = 0.5;
    let newRotation = rotation + e.movementX * sensitivity;
    if (newRotation > 100) newRotation = 0;
    if (newRotation < 0) newRotation = 100;
    setRotation(newRotation);

    const video = videoRef.current;
    if (video.duration && !isNaN(video.duration)) {
      video.currentTime = (newRotation / 100) * video.duration;
    }
  };

  const handleCaptureSnapshot = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/png");
    if (onCapture) onCapture(dataUrl);
  };

  return (
    <div className="flex flex-col gap-4 bg-[var(--color-bg-secondary)] p-4 rounded-xl border border-[var(--color-border-primary)] w-full max-w-lg shadow-2xl transition-all duration-300">
      
      {/* 标题栏 */}
      <div className="flex items-center justify-between border-b border-[var(--color-border-secondary)] pb-3">
        <div className="flex items-center gap-2 text-[var(--color-accent-primary)] font-bold">
          <RotateCw size={18} />
          <span>360° 自由视角 (AI Orbit)</span>
        </div>
        <div className="text-2xs text-[var(--color-text-muted)] bg-[color-mix(in_srgb,var(--color-text-primary)_8%,transparent)] px-2 py-1 rounded">Powered by Vidu</div>
      </div>

      {/* 主视图区域 */}
      <div 
        // ✅ 2. 修改：移除 aspect-video，添加内联样式 aspectRatio
        // w-full 保证宽度撑满，aspectRatio 保证高度自适应
        className="relative w-full bg-black rounded-lg overflow-hidden border border-[var(--color-border-primary)] cursor-ew-resize group select-none transition-all duration-500 ease-in-out"
        style={{ aspectRatio: aspectRatio }} 
        onMouseMove={handleMouseMove}
      >
        {/* 状态 A: 空闲 */}
        {status === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            {initialImage && (
              <img src={initialImage} className="absolute inset-0 w-full h-full object-cover opacity-40 blur-sm" alt="preview" />
            )}
            {/* 功能色：模糊预览图上的遮罩保持黑色系 overlay，文字保持白色以保证任何主题下可读 */}
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-[var(--overlay-light)] backdrop-blur-[2px]">
              <button
                onClick={startOrbitGeneration}
                className="text-[var(--color-accent-contrast)] px-6 py-3 rounded-full font-bold shadow-lg flex items-center gap-2 transition-opacity hover:opacity-90"
                style={{ backgroundColor: 'var(--color-accent-primary)' }}
              >
                <RotateCw size={20} className="animate-spin-slow" /> 生成 3D 空间
              </button>
              <div className="text-2xs text-white mt-2 bg-[var(--overlay-light)] px-2 py-1 rounded">
                预计消耗: 3￥
              </div>
            </div>
          </div>
        )}

        {/* 状态 B: 生成/轮询中 */}
        {(status === "generating" || status === "polling") && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[var(--color-bg-secondary)] z-20">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-[color-mix(in_srgb,var(--color-accent-primary)_30%,transparent)] border-t-[var(--color-accent-primary)] rounded-full animate-spin"></div>
            </div>
            <div className="flex flex-col items-center gap-1">
               <span className="text-sm font-bold text-[var(--color-text-primary)] tracking-wide">
                 {status === "generating" ? "提交任务中..." : "AI 正在构建 3D 场景..."}
               </span>
               <span className="text-xs text-[var(--color-text-muted)]">约需 10~20 秒，请稍候</span>
            </div>
          </div>
        )}

        {/* 状态 C: 失败 */}
        {status === "failed" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[var(--color-bg-secondary)] z-20">
            <AlertCircle size={40} className="text-[var(--color-error)]" />
            <div className="text-center px-8">
              <div className="text-[var(--color-text-primary)] font-bold mb-1">生成失败</div>
              <div className="text-xs text-[var(--color-error)]">{errorMsg}</div>
            </div>
            <button 
              onClick={() => setStatus("idle")}
              className="mt-2 px-4 py-2 bg-[color-mix(in_srgb,var(--color-text-primary)_12%,transparent)] hover:bg-[color-mix(in_srgb,var(--color-text-primary)_20%,transparent)] rounded-lg text-xs text-[var(--color-text-primary)] transition-colors"
            >
              重试
            </button>
          </div>
        )}

        {/* 状态 D: 就绪 */}
        {status === "ready" && videoUrl && (
          <>
            <canvas ref={canvasRef} className="hidden" />
            
            <video 
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full object-cover pointer-events-none animate-in fade-in duration-300"
              muted
              playsInline
              loop
              crossOrigin="anonymous"
              // ✅ 3. 修改：监听元数据加载，动态设置宽高比
              onLoadedMetadata={(e) => {
                 const { videoWidth, videoHeight } = e.target;
                 if (videoHeight > 0) {
                    setAspectRatio(videoWidth / videoHeight);
                 }
                 e.target.currentTime = 0;
              }}
            />

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
               {/* 功能色：视频画面上的提示 pill 保持黑色遮罩+白字，任何主题下对视频内容可读 */}
               <div className="bg-[var(--overlay-medium)] backdrop-blur px-4 py-2 rounded-full text-white text-sm flex items-center gap-2 animate-pulse group-hover:opacity-0 transition-opacity duration-500">
                  <MousePointer2 size={16} /> 按住鼠标左右拖动旋转
               </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-[var(--overlay-heavy)] to-transparent flex gap-4 items-center z-10">
               <input 
                 type="range" 
                 min="0" 
                 max="100" 
                 step="0.1"
                 value={rotation} 
                 onChange={(e) => {
                    setRotation(Number(e.target.value));
                    if(videoRef.current && videoRef.current.duration) {
                        videoRef.current.currentTime = (e.target.value / 100) * videoRef.current.duration;
                    }
                 }}
                 className="flex-1 h-1.5 bg-[color-mix(in_srgb,var(--color-text-primary)_20%,transparent)] rounded-lg appearance-none cursor-pointer accent-[var(--color-accent-primary)] hover:accent-[var(--color-accent-secondary)]"
               />
               <button 
                 onClick={handleCaptureSnapshot}
                 className="bg-[var(--color-accent-primary)] text-[var(--color-accent-contrast)] p-2.5 rounded-full hover:bg-[var(--color-accent-secondary)] hover:scale-110 transition-all shadow-xl flex-shrink-0"
                 title="拍摄当前角度"
               >
                 <Camera size={18} />
               </button>
            </div>
          </>
        )}
      </div>
      
      {/* 底部说明 */}
      {status === "ready" ? (
        <div className="flex justify-between items-center text-2xs text-[var(--color-text-muted)]">
           <span>当前角度: {Math.round(rotation * 3.6)}°</span>
           <span className="text-[color-mix(in_srgb,var(--color-accent-primary)_80%,transparent)]">点击相机图标保存截图</span>
        </div>
      ) : (
        <div className="text-2xs text-[var(--color-text-muted)] leading-tight">
          * 请仔细检查内容。
        </div>
      )}
    </div>
  );
};

export default Orbit3DViewer;